import {
  ASPECT_KEYS,
  ASPECTS,
  type Aspect,
  LANGUAGES,
  type ProjectDto,
  type ProjectSummaryDto,
  SIZE_KEYS,
  SIZES,
  STYLE_PRESETS,
  type VideoSize,
} from '@studio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clapperboard, Copy, Film, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog, NativeSelect } from '@/components/ui/controls';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/form';
import { EmptyState, ErrorAlert, PageHeader, Skeleton } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useCharacters, useProducts } from '@/lib/queries';
import { ago } from '@/lib/utils';

function NewProjectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const nav = useNavigate();
  const { data: products } = useProducts();
  const { data: characters } = useCharacters();
  const [title, setTitle] = useState('');
  const [productId, setProductId] = useState('');
  const [language, setLanguage] = useState('Hindi');
  const [style, setStyle] = useState<string>(STYLE_PRESETS[0]);
  const [aspect, setAspect] = useState<Aspect>('9:16');
  const [size, setSize] = useState<VideoSize>('hd');
  const [castIds, setCastIds] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () =>
      api<ProjectDto>('/api/projects', {
        method: 'POST',
        body: { title, productId: productId || null, language, style, aspect, size, castIds },
      }),
    onSuccess: (p) => {
      onOpenChange(false);
      nav(`/projects/${p.id}?tab=script`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New video</DialogTitle>
          <DialogDescription>One project makes one ad. Everything can be changed later.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Title" className="sm:col-span-2">
            {(p) => (
              <Input
                {...p}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Hair Booster Oil — Diwali ad"
              />
            )}
          </Field>
          <Field label="Product">
            {(p) => (
              <NativeSelect {...p} value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">— none —</option>
                {products?.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.title}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Language of the dialogue">
            {(p) => (
              <NativeSelect {...p} value={language} onChange={(e) => setLanguage(e.target.value)}>
                {LANGUAGES.map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Style">
            {(p) => (
              <NativeSelect {...p} value={style} onChange={(e) => setStyle(e.target.value)}>
                {STYLE_PRESETS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Shape">
            {(p) => (
              <NativeSelect {...p} value={aspect} onChange={(e) => setAspect(e.target.value as Aspect)}>
                {ASPECT_KEYS.map((a) => (
                  <option key={a} value={a}>
                    {ASPECTS[a].label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Size" hint={SIZES[size].hint}>
            {(p) => (
              <NativeSelect {...p} value={size} onChange={(e) => setSize(e.target.value as VideoSize)}>
                {SIZE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {SIZES[k].label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <div className="sm:col-span-2">
            <div className="mb-1.5 text-sm font-medium">Characters</div>
            <div className="flex flex-wrap gap-2">
              {characters?.map((c) => {
                const on = castIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCastIds(on ? castIds.filter((x) => x !== c.id) : [...castIds, c.id])}
                    className={`flex items-center gap-2 rounded-full border px-2 py-1 text-sm ${on ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                  >
                    {c.images[0] && <img src={c.images[0].url} alt="" className="size-6 rounded-full object-cover" />}
                    {c.name}
                  </button>
                );
              })}
              {!characters?.length && (
                <span className="text-sm text-muted-foreground">
                  No characters yet (add them on the Characters page).
                </span>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!title.trim()}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['projects'], queryFn: () => api<ProjectSummaryDto[]>('/api/projects') });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ProjectSummaryDto | null>(null);
  const dup = useMutation({
    mutationFn: (p: ProjectSummaryDto) =>
      api<ProjectDto>(`/api/projects/${p.id}/duplicate`, { method: 'POST', body: { title: `${p.title} (copy)` } }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      nav(`/projects/${p.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (p: ProjectSummaryDto) => api(`/api/projects/${p.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Projects"
        description="Each project is one video ad. Open the demo to see how the Fat Cutter ad was made, or start a new one."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> New video
          </Button>
        }
      />
      {q.error && <ErrorAlert message={errorMessage(q.error)} />}
      {q.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      )}
      {q.data && !q.data.length && (
        <EmptyState
          icon={<Clapperboard />}
          title="No projects yet"
          description="Make your first video ad."
          action={<Button onClick={() => setCreating(true)}>New video</Button>}
        />
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {q.data?.map((p) => (
          <Card key={p.id} className="flex flex-col overflow-hidden">
            <Link to={`/projects/${p.id}`} className="flex flex-1 gap-3 p-3 hover:bg-accent/40">
              <div className="grid h-36 w-20 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                {p.thumbnail ? (
                  <img src={p.thumbnail.url} alt="" className="size-full object-cover" />
                ) : (
                  <Film className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 space-y-1.5">
                <div className="font-medium leading-snug">{p.title}</div>
                <div className="flex flex-wrap gap-1">
                  {p.isDemo && <Badge>demo</Badge>}
                  <Badge variant="secondary">{p.aspect}</Badge>
                  <Badge variant="secondary">{p.language}</Badge>
                  {p.finalVideo && <Badge variant="success">finished video</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{p.product?.title ?? 'No product'}</div>
                <div className="text-xs text-muted-foreground">
                  {p.shotsDone}/{p.shots} shots made · {ago(p.updatedAt)}
                </div>
              </div>
            </Link>
            <div className="flex gap-1 border-t px-2 py-1.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => dup.mutate(p)}
                loading={dup.isPending && dup.variables?.id === p.id}
              >
                <Copy /> Duplicate
              </Button>
              {!p.isDemo && (
                <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => setDeleting(p)}>
                  <Trash2 /> Delete
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
      <NewProjectDialog open={creating} onOpenChange={setCreating} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={`Delete "${deleting?.title}"?`}
        description="Its shots and takes are deleted too. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting)}
      />
    </div>
  );
}
